import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, canonicalStoredFileIdForApiPath } from '../lib/api'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { formatApiDateTimeDisplay } from '../lib/date'

function unwrapData(res) {
  return (res?.data && res.data.data !== undefined) ? res.data.data : res?.data
}

function formatDateTime(iso) {
  return formatApiDateTimeDisplay(undefined, iso)
}

function formatFileSize(size) {
  const n = Number(size)
  if (!Number.isFinite(n) || n <= 0) return ''
  if (n < 1024) return `${n.toLocaleString('ko-KR')} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

function authorLabel(comment) {
  if (comment?.authorRole === 'OPERATOR') return '운영자'
  return '사용자'
}

const DETAIL_CATEGORY_LABELS = {
  CUSTOMER_SUPPORT: {
    GENERAL: '일반문의',
    ERROR: '오류 및 에러',
    BILLING_SUBSCRIPTION: '구매/구독',
    NOTIFICATION_DELIVERY: '알림/발송 문의',
    FILE_DOCUMENT_FAX: '파일/문서/PDF/팩스 문의',
  },
  ACCOUNT_RELATED: {
    LOGIN_AUTH: '로그인/인증',
    ORG_TRANSFER: '조직전환',
    WITHDRAWAL: '회원탈퇴',
    PROFILE: '회원정보',
    ROLE_PERMISSION: '권한/역할 변경',
    SECURITY_ACCESS: '보안/접속 제한',
  },
  UW3NN_EXCEPTION: {
    GENERAL: '일반',
    UW3NN_ADD: '예외질환 추가',
    UW3NN_UPDATE: '예외질환 수정',
    UW3NN_DELETE: '예외질환 삭제/비활성',
    INSURER_RULE: '보험사 기준 문의',
  },
  FEATURE_REQUEST: {
    FEATURE_IMPROVEMENT: '기능 개선 요청',
    FEATURE_ADDITION: '기능 추가 요청',
  },
}

const INQUIRY_CATEGORY_OPTIONS = [
  { id: 'CUSTOMER_SUPPORT', label: '고객지원' },
  { id: 'ACCOUNT_RELATED', label: '계정관련' },
  { id: 'UW3NN_EXCEPTION', label: '예외질환' },
  { id: 'FEATURE_REQUEST', label: '기능개선' },
]

function detailCategoryOptionsFor(category) {
  const labels = DETAIL_CATEGORY_LABELS[category] || DETAIL_CATEGORY_LABELS.CUSTOMER_SUPPORT
  return Object.entries(labels).map(([id, label]) => ({ id, label }))
}

function defaultDetailCategoryFor(category) {
  return detailCategoryOptionsFor(category)[0]?.id || 'GENERAL'
}
function inquiryCategoryLabel(category) {
  if (category === 'ACCOUNT_RELATED') return '계정관련'
  if (category === 'UW3NN_EXCEPTION') return '예외질환'
  if (category === 'FEATURE_REQUEST') return '기능개선'
  return '고객지원'
}

function inquiryDetailCategoryLabel(category, detailCategory) {
  if (!detailCategory) return ''
  return DETAIL_CATEGORY_LABELS[category]?.[detailCategory] || ''
}

async function copyText(text) {
  const value = text ? String(text) : ''
  if (!value) return false
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch {
    // fallback below
  }
  try {
    const input = document.createElement('input')
    input.value = value
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    document.body.removeChild(input)
    return true
  } catch {
    return false
  }
}

export default function SupportInquiryDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isOperator = useMemo(() => (user?.roles || []).includes(Roles.OPERATOR), [user?.roles])

  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const [data, setData] = useState(null)

  const [commentText, setCommentText] = useState('')
  const [replyParentId, setReplyParentId] = useState(null)
  const [commenting, setCommenting] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [copiedField, setCopiedField] = useState('')
  const [attachmentPreviewUrls, setAttachmentPreviewUrls] = useState({})
  const [attachmentPreviewLoading, setAttachmentPreviewLoading] = useState(false)
  const [imageModal, setImageModal] = useState(null)
  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editContent, setEditContent] = useState('')
  const [editCategory, setEditCategory] = useState('CUSTOMER_SUPPORT')
  const [editDetailCategory, setEditDetailCategory] = useState('GENERAL')
  const [editSaving, setEditSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [editingCommentId, setEditingCommentId] = useState(null)
  const [editingCommentText, setEditingCommentText] = useState('')
  const [savingCommentId, setSavingCommentId] = useState(null)
  const [deletingCommentId, setDeletingCommentId] = useState(null)

  const comments = useMemo(() => (Array.isArray(data?.comments) ? data.comments : []), [data?.comments])
  const attachments = useMemo(() => (Array.isArray(data?.attachments) ? data.attachments : []), [data?.attachments])
  const attachmentPreviewKey = useMemo(
    () => attachments.map((file) => `${file?.id || ''}:${file?.fileId || ''}`).join('|'),
    [attachments],
  )
  const replyTarget = replyParentId ? comments.find((comment) => comment.id === replyParentId) : null
  const canComment = Boolean(data) && (isOperator || data?.source !== 'LANDING')
  const detailCategoryText = useMemo(
    () => inquiryDetailCategoryLabel(data?.category, data?.detailCategory),
    [data?.category, data?.detailCategory],
  )
  const editDetailCategoryOptions = useMemo(() => detailCategoryOptionsFor(editCategory), [editCategory])

  async function reload() {
    const res = await api.get(`/inquiries/${id}`)
    const payload = unwrapData(res)
    setData(payload)
    return payload
  }

  useEffect(() => {
    let ignore = false
    async function load() {
      if (!id) return
      setLoading(true)
      setErr('')
      try {
        const res = await api.get(`/inquiries/${id}`)
        const payload = unwrapData(res)
        if (!ignore) {
          setData(payload)
          setCommentText('')
          setReplyParentId(null)
          setEditingCommentId(null)
          setEditingCommentText('')
        }
      } catch (e) {
        console.error(e)
        if (!ignore) setErr('문의 내용을 불러오지 못했습니다. 권한 또는 존재 여부를 확인해 주세요.')
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    load()
    return () => { ignore = true }
  }, [id])

  useEffect(() => {
    let cancelled = false
    const objectUrls = []

    async function loadAttachmentPreviews() {
      if (attachments.length === 0) {
        setAttachmentPreviewUrls((prev) => {
          Object.values(prev || {}).forEach((url) => {
            try { URL.revokeObjectURL(url) } catch { }
          })
          return {}
        })
        setAttachmentPreviewLoading(false)
        return
      }

      setAttachmentPreviewLoading(true)
      const next = {}
      for (const file of attachments) {
        const fileId = file?.fileId
        if (!fileId) continue
        try {
          const pathId = canonicalStoredFileIdForApiPath(fileId)
          const res = await api.get(`/files/${encodeURIComponent(pathId)}/preview`, {
            responseType: 'blob',
            __silent: true,
          })
          if (cancelled) break
          const blob = new Blob([res.data], { type: res.headers?.['content-type'] || file.contentType || 'image/png' })
          const url = URL.createObjectURL(blob)
          objectUrls.push(url)
          next[file.id || file.fileId] = url
        } catch {
          // per-file preview failure keeps the attachment metadata visible
        }
      }

      if (cancelled) {
        objectUrls.forEach((url) => {
          try { URL.revokeObjectURL(url) } catch { }
        })
        return
      }
      setAttachmentPreviewUrls((prev) => {
        Object.values(prev || {}).forEach((url) => {
          try { URL.revokeObjectURL(url) } catch { }
        })
        return next
      })
      setAttachmentPreviewLoading(false)
    }

    void loadAttachmentPreviews()
    return () => {
      cancelled = true
      objectUrls.forEach((url) => {
        try { URL.revokeObjectURL(url) } catch { }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentPreviewKey])

  const onSubmitComment = async () => {
    if (!id) return
    const content = commentText.trim()
    if (!content) return
    try {
      setCommenting(true)
      await api.post(`/inquiries/${id}/comments`, {
        content,
        parentId: replyParentId || null,
      })
      await reload()
      setCommentText('')
      setReplyParentId(null)
    } catch (e) {
      console.error(e)
      setErr('댓글 등록에 실패했습니다.')
    } finally {
      setCommenting(false)
    }
  }

  const onConfirm = async () => {
    if (!id) return
    try {
      setConfirming(true)
      await api.post(`/inquiries/${id}/confirm`, { confirm: true })
      await reload()
      try { window?.toast?.show?.('확인 처리되었습니다.', 'success') } catch { }
    } catch (e) {
      console.error(e)
      setErr('확인 처리에 실패했습니다.')
    } finally {
      setConfirming(false)
    }
  }

  const onComplete = async () => {
    if (!id) return
    try {
      setCompleting(true)
      await api.post(`/inquiries/${id}/complete`)
      await reload()
      try { window?.toast?.show?.('완료 처리되었습니다.', 'success') } catch { }
    } catch (e) {
      console.error(e)
      setErr('완료 처리에 실패했습니다.')
    } finally {
      setCompleting(false)
    }
  }

  const startEdit = () => {
    if (!data?.canEdit) return
    const category = data.category || 'CUSTOMER_SUPPORT'
    setEditTitle(data.title || '')
    setEditContent(data.content || '')
    setEditCategory(category)
    setEditDetailCategory(data.detailCategory || defaultDetailCategoryFor(category))
    setErr('')
    setEditing(true)
  }

  const onEditCategoryChange = (category) => {
    setEditCategory(category)
    setEditDetailCategory(defaultDetailCategoryFor(category))
  }

  const onSaveEdit = async (event) => {
    event.preventDefault()
    if (!id) return
    const title = editTitle.trim()
    const content = editContent.trim()
    if (!title || !content) return
    try {
      setEditSaving(true)
      await api.patch(`/inquiries/${id}`, {
        title,
        content,
        category: editCategory,
        detailCategory: editDetailCategory,
      })
      await reload()
      setEditing(false)
      try { window?.toast?.show?.('문의가 수정되었습니다.', 'success') } catch { }
    } catch (e) {
      console.error(e)
      setErr('문의 수정에 실패했습니다.')
    } finally {
      setEditSaving(false)
    }
  }

  const onDeleteInquiry = async () => {
    if (!id || !data?.canDelete) return
    if (!window.confirm('이 문의를 삭제할까요? 삭제 후에는 복구할 수 없습니다.')) return
    try {
      setDeleting(true)
      await api.delete(`/inquiries/${id}`)
      try { window?.toast?.show?.('문의가 삭제되었습니다.', 'success') } catch { }
      navigate('/support/inquiries')
    } catch (e) {
      console.error(e)
      setErr('문의 삭제에 실패했습니다.')
    } finally {
      setDeleting(false)
    }
  }
  const startEditComment = (comment) => {
    if (!comment?.canEdit) return
    setReplyParentId(null)
    setEditingCommentId(comment.id)
    setEditingCommentText(comment.content || '')
    setErr('')
  }

  const cancelEditComment = () => {
    setEditingCommentId(null)
    setEditingCommentText('')
  }

  const onSaveCommentEdit = async (commentId) => {
    if (!id || !commentId) return
    const content = editingCommentText.trim()
    if (!content) return
    try {
      setSavingCommentId(commentId)
      await api.patch(`/inquiries/${id}/comments/${commentId}`, { content })
      await reload()
      cancelEditComment()
      try { window?.toast?.show?.('댓글이 수정되었습니다.', 'success') } catch { }
    } catch (e) {
      console.error(e)
      setErr('댓글 수정에 실패했습니다.')
    } finally {
      setSavingCommentId(null)
    }
  }

  const onDeleteComment = async (comment) => {
    if (!id || !comment?.id || !comment?.canDelete) return
    if (!window.confirm('이 댓글을 삭제할까요?')) return
    try {
      setDeletingCommentId(comment.id)
      await api.delete(`/inquiries/${id}/comments/${comment.id}`)
      if (editingCommentId === comment.id) cancelEditComment()
      await reload()
      try { window?.toast?.show?.('댓글이 삭제되었습니다.', 'success') } catch { }
    } catch (e) {
      console.error(e)
      setErr('댓글 삭제에 실패했습니다.')
    } finally {
      setDeletingCommentId(null)
    }
  }
  const onCopy = async (label, value) => {
    const ok = await copyText(value)
    if (!ok) return
    setCopiedField(label)
    try { window?.toast?.show?.(`${label} 복사됨`, 'success') } catch { }
    try {
      window.clearTimeout(window.__supportInquiryCopyTimer)
      window.__supportInquiryCopyTimer = window.setTimeout(() => setCopiedField(''), 1500)
    } catch {
      // ignore
    }
  }

  if (loading) {
    return <div className="text-sm text-slate-600">불러오는 중...</div>
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">문의 상세</h1>
          <div className="text-xs text-slate-500 mt-1">비공개 작성자와 운영자만 열람</div>
        </div>
        <div className="flex items-center gap-2">
          <Link className="rounded border px-3 py-2 text-sm hover:bg-slate-50" to="/support/inquiries">목록</Link>
          <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => navigate(-1)}>
            뒤로
          </button>
        </div>
      </div>

      {err && <div className="text-sm text-red-600">{err}</div>}

      {data && (
        <div className="rounded border p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <div className="font-semibold">{data.title}</div>
                {data.source === 'LANDING' && (
                  <span className="rounded px-2 py-0.5 border bg-sky-50 border-sky-200 text-sky-700 text-[11px]">
                    랜딩 문의
                  </span>
                )}
              </div>
              {isOperator && data.source === 'LANDING' && (
                <div className="mt-2 rounded border bg-slate-50 p-3 text-sm">
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    <div>
                      <div className="text-xs font-semibold text-slate-500">이름</div>
                      <div className="text-slate-800">{data.publicName || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-slate-500">연락처</div>
                      <div className="text-slate-800">{data.publicPhone || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-slate-500">이메일</div>
                      <div className="text-slate-800">{data.publicEmail || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-slate-500">회사명/소속</div>
                      <div className="text-slate-800">{data.publicCompanyName || '-'}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {data.publicPhone ? (
                      <a className="rounded border bg-white px-3 py-2 text-xs font-medium hover:bg-slate-100" href={`tel:${data.publicPhone}`}>
                        전화걸기
                      </a>
                    ) : null}
                    {data.publicEmail ? (
                      <a className="rounded border bg-white px-3 py-2 text-xs font-medium hover:bg-slate-100" href={`mailto:${data.publicEmail}`}>
                        메일 작성
                      </a>
                    ) : null}
                    {data.publicPhone ? (
                      <button
                        type="button"
                        className="rounded border bg-white px-3 py-2 text-xs font-medium hover:bg-slate-100"
                        onClick={() => onCopy('연락처', data.publicPhone)}
                      >
                        {copiedField === '연락처' ? '연락처 복사됨' : '연락처 복사'}
                      </button>
                    ) : null}
                    {data.publicEmail ? (
                      <button
                        type="button"
                        className="rounded border bg-white px-3 py-2 text-xs font-medium hover:bg-slate-100"
                        onClick={() => onCopy('이메일', data.publicEmail)}
                      >
                        {copiedField === '이메일' ? '이메일 복사됨' : '이메일 복사'}
                      </button>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2">
              <div className="text-xs text-slate-500">{formatDateTime(data.createdAt)}</div>
              {!editing && (data.canEdit || data.canDelete) && (
                <div className="flex items-center gap-2">
                  {data.canEdit && (
                    <button
                      type="button"
                      className="rounded border px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
                      onClick={startEdit}
                    >
                      수정
                    </button>
                  )}
                  {data.canDelete && (
                    <button
                      type="button"
                      className="rounded border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
                      disabled={deleting}
                      onClick={onDeleteInquiry}
                    >
                      {deleting ? '삭제 중...' : '삭제'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {editing && (
            <form className="rounded border bg-slate-50 p-3 space-y-3" onSubmit={onSaveEdit}>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-sm text-slate-700" htmlFor="support-inquiry-edit-category">카테고리</label>
                  <select
                    id="support-inquiry-edit-category"
                    className="w-full rounded border px-3 py-2 text-sm"
                    value={editCategory}
                    onChange={(event) => onEditCategoryChange(event.target.value)}
                    disabled={editSaving}
                  >
                    {INQUIRY_CATEGORY_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-sm text-slate-700" htmlFor="support-inquiry-edit-detail-category">세부 카테고리</label>
                  <select
                    id="support-inquiry-edit-detail-category"
                    className="w-full rounded border px-3 py-2 text-sm"
                    value={editDetailCategory}
                    onChange={(event) => setEditDetailCategory(event.target.value)}
                    disabled={editSaving}
                  >
                    {editDetailCategoryOptions.map((option) => (
                      <option key={option.id} value={option.id}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm text-slate-700" htmlFor="support-inquiry-edit-title">제목</label>
                <input
                  id="support-inquiry-edit-title"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={editTitle}
                  onChange={(event) => setEditTitle(event.target.value)}
                  disabled={editSaving}
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm text-slate-700" htmlFor="support-inquiry-edit-content">내용</label>
                <textarea
                  id="support-inquiry-edit-content"
                  className="min-h-[140px] w-full rounded border px-3 py-2 text-sm"
                  value={editContent}
                  onChange={(event) => setEditContent(event.target.value)}
                  disabled={editSaving}
                  required
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  className="rounded bg-brand-navy px-4 py-2 text-sm text-white disabled:opacity-60"
                  disabled={editSaving || !editTitle.trim() || !editContent.trim()}
                >
                  {editSaving ? '저장 중...' : '수정 저장'}
                </button>
                <button
                  type="button"
                  className="rounded border bg-white px-4 py-2 text-sm hover:bg-slate-50"
                  onClick={() => setEditing(false)}
                  disabled={editSaving}
                >
                  수정 취소
                </button>
              </div>
            </form>
          )}
          {data?.category === 'UW3NN_EXCEPTION' && (
            <div className="rounded border bg-slate-50 p-3 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <div className="text-xs font-semibold text-slate-500">보험사</div>
                  <div className="text-slate-800">{data.relatedInsurerName || '-'}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500">조건</div>
                  <div className="text-slate-800">{data.relatedConditionSummary || '-'}</div>
                </div>
              </div>
              <div className="mt-2">
                <div className="text-xs font-semibold text-slate-500">안내</div>
                <div className="text-slate-800 whitespace-pre-wrap">{data.relatedGuidance || '-'}</div>
              </div>
            </div>
          )}
          <div className="whitespace-pre-wrap text-sm text-slate-800">{data.content}</div>

          {attachments.length > 0 && (
            <div className="border-t pt-3">
              <div className="mb-2 text-sm font-semibold">첨부 이미지</div>
              {attachmentPreviewLoading && (
                <div className="mb-2 text-xs text-slate-500">이미지 불러오는 중...</div>
              )}
              <div className="flex flex-wrap gap-2">
                {attachments.map((file) => {
                  const previewUrl = attachmentPreviewUrls[file.id || file.fileId]
                  return (
                  <button
                    type="button"
                    key={file.id || file.fileId}
                    className="group w-28 overflow-hidden rounded border bg-white text-left hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-70"
                    disabled={!previewUrl}
                    onClick={() => {
                      if (!previewUrl) return
                      setImageModal({ ...file, previewUrl })
                    }}
                  >
                    <div className="h-20 bg-slate-100">
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt={file.name || '첨부 이미지'}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center px-2 text-center text-[11px] text-slate-500">
                          미리보기 실패
                        </div>
                      )}
                    </div>
                    <div className="space-y-0.5 px-2 py-1.5 text-[11px]">
                      <div className="truncate font-medium text-slate-800 group-hover:text-brand-navy">{file.name || file.fileId}</div>
                      <div className="truncate text-slate-500">{formatFileSize(file.size) || file.contentType || '-'}</div>
                    </div>
                  </button>
                  )
                })}
              </div>
            </div>
          )}

          <div className="text-xs text-slate-500">
            상태: {data.status === 'ANSWERED' ? '답변완료' : '접수'} · 카테고리: {inquiryCategoryLabel(data.category)}
            {detailCategoryText ? <> · 세부: {detailCategoryText}</> : null}
            <> · 수정일: {formatDateTime(data.updatedAt)}</>
            {data?.category === 'UW3NN_EXCEPTION' && (
              <> · 확인: {data.operatorCheckedAt ? `완료(${formatDateTime(data.operatorCheckedAt)})` : '미확인'}</>
            )}
          </div>
          {isOperator && data.status !== 'ANSWERED' && (
            <div className="pt-2">
              <button
                type="button"
                className="rounded bg-emerald-600 px-4 py-2 text-sm text-white disabled:opacity-60"
                disabled={completing}
                onClick={onComplete}
              >
                {completing ? '처리 중...' : '답변 없이 완료 처리'}
              </button>
            </div>
          )}
          {isOperator && data?.category === 'UW3NN_EXCEPTION' && !data.operatorCheckedAt && (
            <div className="pt-2 border-t">
              <button
                type="button"
                className="rounded bg-emerald-600 text-white px-4 py-2 text-sm disabled:opacity-60"
                disabled={confirming}
                onClick={onConfirm}
              >
                {confirming ? '처리 중...' : '확인'}
              </button>
              <div className="text-xs text-slate-500 mt-2">
                예외질환 문의의 확인 버튼을 누르면 미확인 상태가 해제됩니다.
              </div>
            </div>
          )}
        </div>
      )}

      <div className="rounded border p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="font-semibold text-sm">댓글/답글</div>
          {data?.status === 'ANSWERED' ? (
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
              답변완료
            </span>
          ) : (
            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
              답변대기
            </span>
          )}
        </div>

        <div className="space-y-2">
          {comments.length > 0 ? comments.map((comment) => (
            <div
              key={comment.id}
              className={`rounded border bg-white p-3 ${comment.parentId ? 'ml-4 border-l-4 border-l-sky-200' : ''}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2 text-sm">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${comment.authorRole === 'OPERATOR' ? 'bg-sky-50 text-sky-700' : 'bg-slate-100 text-slate-700'}`}>
                    {authorLabel(comment)}
                  </span>
                  <span className="truncate font-medium text-slate-800">{comment.authorName || authorLabel(comment)}</span>
                </div>
                <div className="flex shrink-0 items-center gap-2 text-xs text-slate-500">
                  <span>{formatDateTime(comment.createdAt)}</span>
                  {editingCommentId !== comment.id && (comment.canEdit || comment.canDelete) && (
                    <span className="flex items-center gap-1">
                      {comment.canEdit && (
                        <button
                          type="button"
                          className="rounded border px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          onClick={() => startEditComment(comment)}
                        >
                          수정
                        </button>
                      )}
                      {comment.canDelete && (
                        <button
                          type="button"
                          className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
                          disabled={deletingCommentId === comment.id}
                          onClick={() => onDeleteComment(comment)}
                        >
                          {deletingCommentId === comment.id ? '삭제 중...' : '삭제'}
                        </button>
                      )}
                    </span>
                  )}
                </div>
              </div>
              {editingCommentId === comment.id ? (
                <div className="mt-3 space-y-2">
                  <textarea
                    className="min-h-[96px] w-full rounded border px-3 py-2 text-sm"
                    value={editingCommentText}
                    onChange={(event) => setEditingCommentText(event.target.value)}
                    disabled={savingCommentId === comment.id}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="rounded bg-brand-navy px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
                      disabled={savingCommentId === comment.id || !editingCommentText.trim()}
                      onClick={() => onSaveCommentEdit(comment.id)}
                    >
                      {savingCommentId === comment.id ? '저장 중...' : '저장'}
                    </button>
                    <button
                      type="button"
                      className="rounded border px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
                      disabled={savingCommentId === comment.id}
                      onClick={cancelEditComment}
                    >
                      취소
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 whitespace-pre-wrap text-sm text-slate-800">{comment.content}</div>
              )}
              {canComment && editingCommentId !== comment.id && (
                <div className="mt-2">
                  <button
                    type="button"
                    className="text-xs font-medium text-brand-navy hover:underline"
                    onClick={() => setReplyParentId(comment.id)}
                  >
                    답글
                  </button>
                </div>
              )}
            </div>
          )) : (
            <div className="rounded border border-dashed p-4 text-center text-sm text-slate-500">
              아직 등록된 댓글이 없습니다.
            </div>
          )}
        </div>

        {comments.length === 0 && data?.adminReply && (
          <div className="rounded border bg-slate-50 p-3">
            <div className="mb-1 text-xs font-semibold text-slate-500">기존 운영자 답글</div>
            <div className="whitespace-pre-wrap text-sm text-slate-800">{data.adminReply}</div>
            {data.adminRepliedAt && (
              <div className="mt-2 text-xs text-slate-500">답변일: {formatDateTime(data.adminRepliedAt)}</div>
            )}
          </div>
        )}

        {canComment && (
          <div className="pt-2 border-t space-y-2">
            {replyTarget && (
              <div className="flex items-center justify-between gap-2 rounded border bg-sky-50 px-3 py-2 text-xs text-sky-800">
                <div className="truncate">
                  {replyTarget.authorName || authorLabel(replyTarget)} 댓글에 답글 작성 중
                </div>
                <button
                  type="button"
                  className="rounded border border-sky-200 bg-white px-2 py-1 text-xs hover:bg-sky-100"
                  onClick={() => setReplyParentId(null)}
                >
                  취소
                </button>
              </div>
            )}
            <textarea
              className="w-full rounded border px-3 py-2 min-h-[120px]"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={replyParentId ? '답글을 입력하세요.' : '댓글을 입력하세요.'}
            />
            <button
              type="button"
              className="rounded bg-brand-navy text-white px-4 py-2 text-sm disabled:opacity-60"
              disabled={commenting || !commentText.trim()}
              onClick={onSubmitComment}
            >
              {commenting ? '등록 중...' : (replyParentId ? '답글 등록' : '댓글 등록')}
            </button>
          </div>
        )}
      </div>

      {imageModal && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="첨부 이미지 보기"
          onClick={() => setImageModal(null)}
        >
          <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded border bg-white shadow-xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-900">{imageModal.name || imageModal.fileId || '첨부 이미지'}</div>
                <div className="text-xs text-slate-500">
                  {[imageModal.contentType, formatFileSize(imageModal.size)].filter(Boolean).join(' · ')}
                </div>
              </div>
              <button
                type="button"
                className="shrink-0 rounded border px-3 py-1.5 text-sm hover:bg-slate-50"
                onClick={() => setImageModal(null)}
              >
                닫기
              </button>
            </div>
            <div className="flex min-h-0 flex-1 items-center justify-center bg-slate-950 p-3">
              <img
                src={imageModal.previewUrl}
                alt={imageModal.name || '첨부 이미지'}
                className="max-h-[calc(92vh-6rem)] max-w-full object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
